import Vue from 'vue'
import { API_URL } from '@/config'
import router from '@/router'
import { getValues, prepareValues } from '@/lib/values'

export const session_presentations = {

  namespaced: true,

  state: {
    assigned: [],
    filters: [
      { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
    ],
    options: {
      title: { label: 'title', element: 'text' },
    },
    presentations: [],
    duration_all: null,
    including_all: null,
    order: null,
    duration: null,
    values: {},
    entities: [],
  },

  getters: {
    assigned: state => state.assigned,
    filters: state => state.filters,
    options: state => state.options,
    presentations: state => state.presentations,
    duration_all: state => state.duration_all,
    including_all: state => state.including_all,
    order: state => state.order,
    duration: state => state.duration,
    values: state => state.values,
    entities: state => state.entities,
  },

  mutations: {
    setAssigned(state, assigned) { state.assigned = assigned },
    setOptions(state, options) { state.options = options },
    setPresentations(state, presentations) {
      presentations = presentations.map(presentation => {
        presentation.order = null;
        return presentation;
      })
      state.presentations = presentations;
    },
    setDurationAll(state, duration_all) {
      state.duration_all = duration_all;
    },
    setIncludingAll(state, including_all) {
      state.including_all = including_all;
    },
    setOrder(state, order) { state.order = order },
    setDuration(state, duration) { state.duration = duration },
    setValues(state, values) { 
      for (let column in values) {
        Vue.set(state.values, column, values[column]);
      }
    },
    setEntities(state, entities) { state.entities = entities },
    clearFilters(state) {
      state.filters = [
        { column: 'title', label: 'title', options: { type: false }, value: '', status: true },
      ];
      state.options = {
        title: { label: 'title', element: 'text' },
      };
    },
  },

  actions: {

    getAssigned: () => Vue.axios.get(API_URL + '/sessions/' + router.currentRoute.params.id + '/presentations'),

    getOptions: () => Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Presentation'],
      filter_display: true,
    }}),

    getEntities: () =>  Vue.axios.get(API_URL + '/entities', { params: {
      models: ['App\\Models\\Presentation'],
      form_display: true,
      form_required: true,
    }}),

    async getAssignedPresentations({ state, commit, dispatch }) {
      commit('clearResponse', {}, { root: true });
      commit('setAssigned', []);
      const [assigned, options, entities] = await Promise.all([
        dispatch('getAssigned'),
        dispatch('getOptions'),
        dispatch('getEntities'),
      ]);
      commit('setProgress', false, { root: true });
      commit('setAssigned', assigned.data);
      commit('setOptions', options.data['App\\Models\\Presentation'].filters);
      commit('setValues', getValues('presentation', entities.data['App\\Models\\Presentation'], 'content'));
      commit('setEntities', entities.data['App\\Models\\Presentation']);

      state.options.type = {
        format: 'wording',
        label: 'type',
        element: 'radio',
        custom: true,
        options: [
          { value: 'abstract', name: 'abstract' },
          { value: 'presentation', name: 'presentation' },
        ],
      };
      state.options.assigned = {
        format: 'wording',
        label: 'assigned',
        element: 'radio',
        custom: true,
        options: [
          { value: 'yes', name: 'yes' },
          { value: 'no', name: 'no' },
        ],
      };
    },

    async addPresentations({ state, rootState, commit }, params) {
      if (!rootState.progress && params.presentations.length > 0) {
        let ids = params.presentations.reduce((carry, pval) => { carry.push(pval.id); return carry; }, []);
        const assigned = await Vue.axios.post(API_URL + '/sessions/' + router.currentRoute.params.id + '/presentations', {
          session_id: router.currentRoute.params.id,
          presentations: params.presentations,
        });
        commit('setPresentations', state.presentations.filter(presentation => !ids.includes(presentation.id)));
        commit('setProgress', false, { root: true });
        commit('clearResponse', {}, { root: true });
        commit('setValues', getValues('presentation', state.entities, 'content'));
        commit('setAssigned', assigned.data);
      }
    },

    async updatePresentations({ rootState, state, commit }) {
      if (!rootState.progress) {
        const assigned = await Vue.axios.put(API_URL + '/sessions/' + router.currentRoute.params.id + '/presentations', state.assigned);
        commit('setProgress', false, { root: true });
        commit('setAssigned', assigned.data);
      }
    },

    async generatePresentationCodes({ rootState, commit }) {
      if (!rootState.progress) {
        const assigned = await Vue.axios.put(API_URL + '/sessions/' + router.currentRoute.params.id + '/presentations/codes');
        commit('setProgress', false, { root: true });
        commit('setAssigned', assigned.data);
      }
    },

    async removePresentation({ rootState, getters, commit }, id) {
      if (!rootState.progress) {
        const assigned = await Vue.axios.delete(API_URL + '/sessions/' + router.currentRoute.params.id + '/presentations/' + id);
        const exclude = assigned.data.reduce((carry, session_presentation) => {
          carry.push(session_presentation.presentation_id);
          return carry;
        }, []);
        const presentations = await Vue.axios.get(API_URL + '/presentations', {
          params: { with: 'persons.person', filters: getters.filters.concat([ { column: 'id', not: true, value: exclude.join(','), status: true }, ]), limit: 200, }
        });
        commit('setAssigned', assigned.data);
        commit('setPresentations', presentations.data.items);
        commit('setProgress', false, { root: true });
      }
    },

    async updatePresentationTimes({ rootState, state, dispatch }) {
      if (!rootState.progress && (state.duration_all || state.including_all)) {
        if (state.duration_all) {
          state.assigned = state.assigned.map(presentation => {
              presentation.duration = state.duration_all;
              return presentation;
          });
        }
        if (state.including_all) {
          state.assigned = state.assigned.map(presentation => {
              presentation.including = state.including_all;
              return presentation;
          });
        }
        state.duration_all = null;
        state.including_all = null;
        dispatch('updatePresentations');
      }
    },

    async getPresentations({ state, rootState, getters, commit }) {
      if (!rootState.progress) {
        let assigned = state.assigned.reduce((carry, session_presentation) => { 
          carry.push(session_presentation.presentation_id);
          return carry;
        }, []);
        commit('setPresentations', []);
        let filters = (state.assigned.length > 0 ) 
          ? getters.filters.concat([{ column: 'id', not: true, value: assigned.join(','), status: true }])
          : getters.filters;
        const presentations = await Vue.axios.get(API_URL + '/presentations', {
          params: {
            with: 'persons.person',
            filters: filters,
            limit: 200,
          }
        })
        .catch(() => {
          commit('setProgress', false, { root: true });
        });
        commit('setProgress', false, { root: true });
        commit('setPresentations', presentations.data.items);
      }
    },

    async validatePresentation({ state, rootState, commit, dispatch }) {
      if (!rootState.progress) {
        try {
          await Vue.axios.post(API_URL + '/validate?model=App\\Models\\Presentation', state.values, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          })
        } catch (error) {
          commit('setProgress', false, { root: true });
          commit('setResponse', { status: true, type: 'error', message: 'form_error', errors: error.response.data.errors }, { root: true });
          return;
        }
        dispatch('createPresentation', prepareValues(state.values, 'content_'));
      }
    },

    async createPresentation({ state, commit, dispatch }, values) {
      const presentation = await Vue.axios.post(API_URL + '/presentations', values);
      commit('setProgress', false, { root: true });
      dispatch('addPresentations', {
        presentations: [{
          id: presentation.data.id,
          order: state.order,
          duration: state.duration,
        }],
      });
    },

  }
}
